import React, { useCallback } from 'react';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../../offline/useRevalidateOnSync';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import debug from 'debug';

// Styles
import { Container } from './styles';
import { ContainerButton } from '../../components/MainStyledComponents';

//Components
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';

// Types
import { MultimediaCheckBoxTable } from '../../../../../components/display/tables/MultimediaCheckBoxTable';
import {
  tableIds,
  takeBase,
} from '../../../../../components/display/tables/types';
import { firstPageTake } from '../../../../../utils/cursor-take.helper';

// GraphQl
import { client } from '../../../../../gql/client';
import { FETCH_ORDERS } from '../../../../../gql/queries/orders/orders';
import { dataTypes } from '../../../../../components/display/tables/MultimediaCheckBoxTable/types';

// Redux
import { getCursor } from '../../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { openModal } from '../../../../../state/actions/ui/modal';
import { store } from '../../../../../state/configureStore';
import { modalIds } from '../../../../../components/modals/SuperModal/types';

// Hooks
import { usePersistentSelection } from '../../../../../hooks/usePersistentSelection';
import { usePollingRevalidation } from '../../../../../hooks/usePollingRevalidation';

const log = debug('rois:multimedia-page');

if (import.meta.env.DEV) {
  debug.enable('rois:multimedia-page*');
}

// Página global de Multimedia (LISTADO en tabla — no confundir con el grid de
// fotos de un contenedor, `WorkOrderDetailed/MultimediaPage`), parametrizable
// para reutilizarse como pestaña de un LISTADO de módulo. Todos los props son
// opcionales y sus defaults reproducen el comportamiento global byte-idéntico.
// `origin`/`returnTo` no tienen consumidor (MultimediaGridGeneralView no lee
// contexto de navegación) — se aceptan por paridad con las otras 3 páginas.
//
// `containerTypes` filtra por TIPO de origen (pestaña "Multimedia" de los
// listados de RV y de Campañas). Ver el mismo comentario en ActionsPage.
//
// `containerId` (filtro por un container CONCRETO) se quedó SIN consumidores
// al retirarse las pestañas del detalle de campaña: hoy los llamadores — el
// listado de Campañas y el del módulo RV — usan `containerTypes`. Se conserva
// la parametrización, que es lo que hace reutilizable esta página.
export type ContextualListProps = {
  containerId?: string;
  containerTypes?: string[];
  tableId?: string;
  origin?: string;
  returnTo?: string;
};

export const MultimediaPage = ({
  containerId,
  containerTypes,
  tableId = tableIds.MULTIMEDIA,
  origin: _origin = 'multimedia-table',
  returnTo: _returnTo,
}: ContextualListProps = {}) => {
  const { t } = useTranslation(['pages', 'workOrders']);
  const data = useAsyncValue() as dataTypes;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const revalidator = useRevalidator();
  useRevalidateOnSync();

  const {
    rowSelection,
    onRowSelectionChange,
    selectedCount,
    selectAllMode,
    excludedIds,
    selectedRows,
    handleSelectAll,
    handleClearSelection,
  } = usePersistentSelection({
    dataAll: data?.dataOrderWithMultimedia ?? [],
    totalCount: data?.totalCount ?? 0,
  });

  const handleSeeOrder = useCallback(() => {
    if (selectedRows[0]) {
      // Fase 2 nav Trabajo/Revisión — antes navegaba con path relativo
      // (`./${id}`), que solo resuelve bien montado en `/work-orders/multimedia`.
      // Absoluto es byte-idéntico ahí (misma resolución) y además correcto
      // si esta página se monta en otra ruta (pestaña de campaña).
      navigate(`/work-orders/multimedia/${selectedRows[0]}`);
    }
  }, [selectedRows, navigate]);

  const handleDownloadPhotos = useCallback(() => {
    const currentCursor = getCursor(tableId)(store.getState());
    // Drop `id` (the tableId — 'multimedia'), `take`, `cursor`, and the
    // cursor's own `containerId`/`containerTypes`: none are valid
    // FilterOrderList fields (well, they are, but see below). The remaining
    // keys are the actual filters the backend expects (state, type,
    // fromDate, concessionId, …).
    // Banderitas (spec 2026-10-22 § 8.3): `onlyFlagged` viaja en
    // `filterFields` por el spread, así que «seleccionar todo» con «Solo
    // marcadas» vuelve a pedir SOLO las marcadas (DownloadMultimediaPhotosModal
    // → FETCH_ORDERS con `...filters`).
    const {
      id: _id,
      take: _take,
      cursor: _cursor,
      containerId: _cursorContainerId,
      containerTypes: cursorContainerTypes,
      ...filterFields
    } = currentCursor || ({} as any);
    // Fase 2 nav Trabajo/Revisión (LOW finding, review T2, 2026-07-12) — el
    // PROP `containerId` manda sobre el del cursor: en modo contextual
    // (pestaña de campaña) el cursor solo tiene containerId sincronizado
    // DESPUÉS de que el loader/onReFetchData corra; leer directo del prop
    // evita depender de esa carrera. Fase 3 nav rework — mismo criterio para
    // `containerTypes`.
    // Fase 4 nav Trabajo/Revisión — en la tabla GLOBAL el prop está vacío:
    // cae al `containerTypes` YA presente en el cursor (filtro "Origen"
    // user-driven, sin la carrera de containerId de arriba porque aquí no
    // hay loader que lo sincronice — el propio filtro escribe el cursor).
    const effectiveContainerTypes = containerTypes?.length
      ? containerTypes
      : cursorContainerTypes;
    dispatch(
      openModal(modalIds.DOWNLOAD_MULTIMEDIA_PHOTOS, {
        selectedRows: selectAllMode ? [] : selectedRows,
        selectAll: selectAllMode,
        excludedIds: selectAllMode ? [...excludedIds] : [],
        filters: {
          ...filterFields,
          ...(containerId && { containerId }),
          ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
        },
        totalCount: data?.totalCount ?? 0,
      })
    );
  }, [
    selectedRows,
    dispatch,
    selectAllMode,
    excludedIds,
    data?.totalCount,
    tableId,
    containerId,
    containerTypes,
  ]);

  const onReFetchData = useCallback(async () => {
    // Always read from store.getState() — avoids stale closure
    const currentCursor = getCursor(tableId)(store.getState());
    log('🔄 onReFetchData - currentCursor:', currentCursor);
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    // Fase 4 nav Trabajo/Revisión — `containerTypes` prop (estático, RV) gana
    // sobre el cursor; en la tabla GLOBAL sale del filtro "Origen".
    const effectiveContainerTypes = containerTypes?.length
      ? containerTypes
      : currentCursor.containerTypes;
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    await client.query({
      query: FETCH_ORDERS,
      variables: {
        input: {
          take: effectiveTake,
          isMultimedia: true,
          cursor: '',
          orderBy: currentCursor.orderBy ?? 'desc',
          sortField: currentCursor.sortField,
          states: currentCursor.states,
          type: currentCursor.type,
          fromDate: currentCursor.fromDate,
          toDate: currentCursor.toDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          roadId: currentCursor.roadId,
          userIds: currentCursor.userIds,
          concessionId: currentCursor.concessionId,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          predefinedOrderId: currentCursor.predefinedOrderId,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor.onlyFlagged,
          // GOTCHA (review T1): omitir en vez de null/undefined explícito —
          // `containerId: null` SÍ entra en la keyArgs de Apollo y partiría
          // la caché histórica sin containerId (ver policies.ts). El campo
          // `orders` de Apollo está COMPARTIDO con WorkOrdersPage — la
          // partición de caché por containerId/containerTypes es aditiva y
          // no la afecta. Misma regla para `containerTypes` (Fase 3 nav
          // rework, pestaña "Multimedia" del listado del módulo RV).
          ...(containerId && { containerId }),
          ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
        },
      },
      fetchPolicy: 'network-only',
    });

    const setCursorData = {
      take: effectiveTake,
      isMultimedia: true,
      orderBy: currentCursor?.orderBy ?? 'desc',
      sortField: currentCursor?.sortField,
      cursor: '',
      states: currentCursor?.states,
      type: currentCursor?.type,
      fromDate: currentCursor?.fromDate,
      toDate: currentCursor?.toDate,
      indexType: currentCursor?.indexType,
      indexExpedition: currentCursor?.indexExpedition,
      classification: currentCursor?.classification,
      roadId: currentCursor.roadId,
      userIds: currentCursor?.userIds,
      concessionId: currentCursor?.concessionId,
      capitolId: currentCursor?.capitolId,
      subCapitolId: currentCursor?.subCapitolId,
      predefinedOrderId: currentCursor?.predefinedOrderId,
      pkInitKm: currentCursor?.pkInitKm,
      pkInitMeter: currentCursor?.pkInitMeter,
      pkEndKm: currentCursor?.pkEndKm,
      pkEndMeter: currentCursor?.pkEndMeter,
      onlyFlagged: currentCursor?.onlyFlagged,
      // Redux (no Apollo): full-replace de la slice del cursor — se escribe
      // el containerId/containerTypes del prop para no perderlos en el
      // replace; undefined en el path global.
      containerId,
      containerTypes: effectiveContainerTypes,
    };

    log('🔄 onReFetchData - setCursorData (isMultimedia=%s):', setCursorData.isMultimedia, setCursorData);
    dispatch(setCursor(tableId, setCursorData));

    revalidator.revalidate();
  }, [tableId, containerId, containerTypes]);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  // Polling: refresh page-1 cache (network-only) without moving the user
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableId)(store.getState());
    const effectiveContainerTypes = containerTypes?.length
      ? containerTypes
      : currentCursor.containerTypes;
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    await client.query({
      query: FETCH_ORDERS,
      variables: {
        input: {
          take: effectiveTake,
          isMultimedia: true,
          cursor: '',
          orderBy: currentCursor.orderBy ?? 'desc',
          sortField: currentCursor.sortField,
          states: currentCursor.states,
          type: currentCursor.type,
          fromDate: currentCursor.fromDate,
          toDate: currentCursor.toDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          roadId: currentCursor.roadId,
          userIds: currentCursor.userIds,
          concessionId: currentCursor.concessionId,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          predefinedOrderId: currentCursor.predefinedOrderId,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor.onlyFlagged,
          ...(containerId && { containerId }),
          ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, [tableId, containerId, containerTypes]);
  usePollingRevalidation(onPollingRefresh);

  return (
    <Container>
      <MultimediaCheckBoxTable
        idCss="historical-multimedia"
        titleText={t('pages:workOrders.multimedia')}
        data={data}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableId}
        selectionMode="checkbox"
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        selectAllMode={selectAllMode}
        excludedIds={excludedIds}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        selectedCount={selectedCount}
        showFlagColumn
        showOnlyFlaggedFilter
      />
      <ContainerButton>
        <MainButton
          variant="secondary"
          text={t('pages:workOrders.viewPhotos', 'Ver fotos')}
          disabled={selectedCount !== 1}
          onClick={handleSeeOrder}
        />
        <MainButton
          text={t('pages:workOrders.downloadPhotos')}
          disabled={selectedCount === 0}
          onClick={handleDownloadPhotos}
        />
      </ContainerButton>
    </Container>
  );
};
